import { Alert } from '../../components/atoms/Alert'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Paginator } from '../../components/molecules/Paginator'
import { StockInOutCharts } from './StockInOutCharts'
import { StockLedgerTable } from './StockLedgerTable'
import { useVariantStockHistory } from '../../../viewmodels/useStockHistory'
import { useOrganisations } from '../../../viewmodels/useOrganisations'

/**
 * /warehouse/variant-stock-history — per-variant stock ledger + the current-year
 * monthly/quarterly In-vs-Out charts. Pick a product, then a variant.
 */
export function VariantStockHistoryPage(): JSX.Element {
  const vm = useVariantStockHistory()
  const orgsVm = useOrganisations()

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-h2 text-charcoal">Variant Stock History</h1>
        <div className="flex flex-wrap items-center gap-3">
          {/* Organisation filter (migration 0115). Default = "Master
              (Uyyamadilu)" so the operator sees their own warehouse
              pool out of the box; switching to any tenant exposes
              that tenant's per-org stock pool + ledger. */}
          <div className="w-full sm:w-auto sm:min-w-[14rem]">
            <Select
              value={vm.organisationId ?? ''}
              onChange={(e) => vm.setOrganisationId(e.target.value === '' ? null : e.target.value)}
              disabled={orgsVm.isLoading}
              aria-label="Organisation"
            >
              <option value="">Master (Uyyamadilu)</option>
              {orgsVm.items
                .filter((o) => !o.is_master)
                .map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
            </Select>
          </div>
          <div className="w-full sm:w-auto sm:min-w-[14rem]">
            <Select
              value={vm.productId ?? ''}
              onChange={(e) => {
                vm.setProductId(e.target.value === '' ? null : e.target.value)
                vm.setVariantId(null)
              }}
              aria-label="Product"
            >
              <option value="">— Select a product —</option>
              {vm.products.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.category_name})</option>
              ))}
            </Select>
          </div>
          <div className="w-full sm:w-auto sm:min-w-[12rem]">
            <Select
              value={vm.variantId ?? ''}
              onChange={(e) => vm.setVariantId(e.target.value === '' ? null : e.target.value)}
              aria-label="Variant"
              disabled={vm.productId === null}
            >
              <option value="">— Select a variant —</option>
              {vm.variants.map((v) => (
                <option key={v.variant_id} value={v.variant_id}>
                  {v.axes_label === null || v.axes_label === '' ? 'default' : v.axes_label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button type="button" className="underline underline-offset-2 hover:text-charcoal" onClick={() => vm.reload()}>
            Retry
          </button>
        </Alert>
      ) : null}

      {vm.variantId === null ? (
        <Placeholder>
          {vm.productId === null
            ? 'Pick a product, then a variant, to see its stock movements and charts.'
            : 'Pick a variant above to see its stock movements and charts.'}
        </Placeholder>
      ) : (
        <>
          <StockInOutCharts
            year={vm.chart.year}
            monthly={vm.chart.monthly}
            quarterly={vm.chart.quarterly}
            isLoading={vm.chart.isLoading}
            error={vm.chart.error}
            onRetry={vm.chart.reload}
          />

          {vm.isLoading ? (
            <div className="flex items-center justify-center py-12"><Spinner /></div>
          ) : vm.ledger.length === 0 ? (
            <Placeholder>No stock movements recorded for this variant yet.</Placeholder>
          ) : (
            <>
              <StockLedgerTable items={vm.ledger} showVariant={false} />
              <Paginator
                page={vm.page}
                pageSize={vm.pageSize}
                total={vm.total}
                onPageChange={vm.setPage}
                disabled={vm.isLoading}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}
